import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Feed from "@/pages/Feed";
import Browse from "@/pages/Browse";
import RecipeDetail from "@/pages/RecipeDetail";
import CookMode from "@/pages/CookMode";
import Create from "@/pages/Create";
import Planner from "@/pages/Planner";
import Supplies from "@/pages/Supplies";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Admin from "@/pages/Admin";
import CreatorDashboard from "@/pages/CreatorDashboard";
import PremiumRequired from "@/components/PremiumRequired";
import BottomNav from "@/components/BottomNav";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-nerd-light/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-nerd-light/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function Premium({ children, feature }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-nerd-light/50">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_premium) return <PremiumRequired feature={feature} />;
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-nerd-bg text-nerd-light">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/cook/:id" element={<CookMode />} />
            <Route
              path="*"
              element={
                <>
                  <Navbar />
                  <Routes>
                    <Route path="/" element={<Feed />} />
                    <Route path="/browse" element={<Browse />} />
                    <Route path="/recipe/:id" element={<RecipeDetail />} />
                    <Route path="/create" element={<Premium feature="The Creative Agent"><Create /></Premium>} />
                    <Route path="/planner" element={<Premium feature="The Meal Planner"><Planner /></Premium>} />
                    <Route path="/supplies" element={<Premium feature="Supplies"><Supplies /></Premium>} />
                    <Route path="/submit" element={<Navigate to="/creator" replace />} />
                    <Route path="/creator" element={<Protected><CreatorDashboard /></Protected>} />
                    <Route path="/creator/signup" element={<Register />} />
                    <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                    <Route path="/profile" element={<Protected><Profile /></Protected>} />
                    <Route path="/settings" element={<Protected><Settings /></Protected>} />
                  </Routes>
                  <BottomNav />
                </>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors theme="dark" />
    </div>
  );
}

export default App;
